"use client";

import Link from "next/link";
import { ChangeEvent, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import imageCompression from "browser-image-compression"; // Compressed Image Library
import {
  User, Mail, Phone, Lock, Camera, CheckCircle2,
  XCircle, Loader2, ArrowLeft, KeyRound, ShieldCheck, Send
} from "lucide-react";

export default function RegisterPage() {
  const router = useRouter();

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [profileImage, setProfileImage] = useState(""); // Base64 string

  // OTP & Verification states
  const [otpSent, setOtpSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [timer, setTimer] = useState(0);

  // Status states
  const [loading, setLoading] = useState(false);
  // Step4508_REGISTER_POLICY_CHECKBOXES
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAcknowledged, setPrivacyAcknowledged] = useState(false);

  // Resend countdown timer
  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  // ==========================================
  // 1. IMAGE COMPRESSION (Max 100 KB)
  // ==========================================
  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const options = {
        maxSizeMB: 0.1, // 100 KB limit
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };

      const compressedFile = await imageCompression(file, options);
      
      const reader = new FileReader();
      reader.readAsDataURL(compressedFile);
      reader.onloadend = () => {
        setProfileImage(reader.result as string);
      };
    } catch (error) {
      console.error("Image compression error:", error);
      alert("Image compress karne mein problem aayi.");
    }
  };

  // ==========================================
  // 2. SEND OTP LOGIC
  // ==========================================
  async function handleSendOtp() {
    const cleanEmail = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!cleanEmail || !emailRegex.test(cleanEmail)) {
      alert("Please enter a valid email address.");
      return;
    }

    try {
      setSendingOtp(true);
      const res = await fetch("/api/auth/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: cleanEmail }),
      });

      if (res.ok) {
        setOtpSent(true);
        setTimer(60);
      } else {
        const data = await res.json();
        alert(data.message || "Failed to send OTP.");
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong while sending OTP.");
    } finally {
      setSendingOtp(false);
    }
  }

  // ==========================================
  // 3. VERIFY OTP LOGIC
  // ==========================================
  async function handleVerifyOtp() {
    if (!otp) return alert("Please enter the OTP.");
    
    try {
      setVerifyingOtp(true);
      const res = await fetch("/api/auth/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });

      if (res.ok) {
        setEmailVerified(true);
      } else {
        alert("Invalid OTP. Please try again.");
      }
    } catch (error) {
      alert("Error verifying OTP.");
    } finally {
      setVerifyingOtp(false);
    }
  }

  // ==========================================
  // 4. SUBMIT FORM LOGIC
  // ==========================================
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!emailVerified) {
      alert("Please verify your email first.");
      return;
    }

    if (!termsAccepted || !privacyAcknowledged) {
      alert("Please agree to the Terms and acknowledge the Privacy Policy.");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          mobile,
          password,
          profileImage,
          termsAccepted,
          privacyAcknowledged,
        }),
      });

      if (res.ok) {
        alert("Registration successful!");
        router.push("/login");
      } else {
        const data = await res.json();
        alert(data.message || "Registration failed");
      }
    } catch (error) {
      alert("Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        
        {/* Header */}
        <div className="text-center">
          <Link href="/" className="inline-flex items-center text-blue-600 hover:text-blue-500 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Create an Account</h2>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          
          {/* Profile Image Picker */}
          <div className="flex flex-col items-center">
            <label htmlFor="image-upload" className="cursor-pointer group relative">
              <div className="w-24 h-24 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50 group-hover:bg-gray-100 transition">
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-8 h-8 text-gray-400" />
                )}
              </div>
            </label>
            <input 
              id="image-upload" 
              type="file" 
              accept="image/*" 
              className="hidden" 
              onChange={handleImageUpload} 
            />
            <p className="mt-2 text-sm text-gray-500">Upload Profile Photo</p>
          </div>

          <div className="space-y-4">
            {/* Name */}
            <div className="relative">
              <User className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
              <input 
                type="text" 
                required 
                value={name} 
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="Full Name" 
              />
            </div>

            {/* Mobile */}
            <div className="relative">
              <Phone className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
              <input 
                type="tel" 
                required 
                value={mobile} 
                onChange={(e) => setMobile(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="Mobile Number" 
              />
            </div>

            {/* Email with OTP Action */}
            <div className="relative">
              <Mail className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
              <input 
                type="email" 
                required 
                disabled={emailVerified}
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-24 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="Email Address" 
              />
              {!emailVerified && (
                <button 
                  type="button" 
                  onClick={handleSendOtp} 
                  disabled={sendingOtp || timer > 0}
                  className="absolute right-2 top-1.5 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded hover:bg-blue-200 disabled:opacity-50"
                >
                  {sendingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : timer > 0 ? `${timer}s` : "Send OTP"}
                </button>
              )}
              {emailVerified && <CheckCircle2 className="absolute right-3 top-3 w-5 h-5 text-green-500" />}
            </div>

            {/* OTP Input (Shows only if OTP is sent & not verified) */}
            {otpSent && !emailVerified && (
              <div className="relative flex space-x-2">
                <div className="relative flex-1">
                  <ShieldCheck className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
                  <input 
                    type="text" 
                    value={otp} 
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                    placeholder="Enter OTP" 
                  />
                </div>
                <button 
                  type="button" 
                  onClick={handleVerifyOtp} 
                  disabled={verifyingOtp}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {verifyingOtp ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify"}
                </button>
              </div>
            )}

            {/* Password */}
            <div className="relative">
              <Lock className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
              <input 
                type="password" 
                required 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="Create Password" 
              />
            </div>
          </div>

          <div className="space-y-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-gray-800">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(event) => setTermsAccepted(event.target.checked)}
                className="mt-1 h-4 w-4"
              />
              <span>
                I have read and agree to the{" "}
                <Link
                  href="/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-blue-700 underline"
                >
                  Terms &amp; Conditions
                </Link>.
              </span>
            </label>

            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={privacyAcknowledged}
                onChange={(event) => setPrivacyAcknowledged(event.target.checked)}
                className="mt-1 h-4 w-4"
              />
              <span>
                I have read and acknowledge the{" "}
                <Link
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-blue-700 underline"
                >
                  Privacy Policy
                </Link>.
              </span>
            </label>
          </div>

          <button 
            type="submit" 
            disabled={
              loading ||
              !emailVerified ||
              !termsAccepted ||
              !privacyAcknowledged
            }
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Register Account"}
          </button>
        </form>

        {/* Step4495_REGISTER_LEGAL_LINKS */}
        <div className="border-t border-gray-200 pt-4 text-center text-sm text-gray-600">
          <p>
            Before registering, please read our development-draft policies:
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <Link
              href="/privacy-policy"
              className="font-semibold text-blue-700 underline hover:text-blue-900"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms-and-conditions"
              className="font-semibold text-blue-700 underline hover:text-blue-900"
            >
              Terms &amp; Conditions
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}